import { useMemo } from 'react';
import { AssetType, AssignedTag } from 'src/features/sites/types/types';
import { useValveData } from 'src/features/sites/hooks';
import { useGetMultipleLatestReadings } from 'src/hooks/reactQueryApiHooks';
import { STATE } from 'src/constants';
import { getStateOrModeData } from 'src/features/widgets/utils';

type Props = {
  asset: AssetType;
};

export const useValveDataHook = ({ asset }: Props) => {
  const { valveLiveData } = useValveData({ asset: asset! });

  const liveState = valveLiveData.state;

  const itemsForHook = useMemo(
    () =>
      asset?.assignedTags?.map((tag) => ({
        id: tag.id,
        assignedAlarmGatewayId: tag.assignedGatewayId ?? null,
        assignedAlarmTagId: tag.assignedTagId ?? null,
      })) ?? [],
    [asset?.assignedTags]
  );

  const { readingsMap } = useGetMultipleLatestReadings({ items: itemsForHook });

  const readingsArray = useMemo(() => {
    if (!asset?.assignedTags) return [];
    return Object.entries(readingsMap)
      .map(([id, values]) => {
        const tag = asset?.assignedTags?.find((t) => t.id === id);
        if (!tag) return null;
        return { gatewayId: tag.assignedGatewayId!, values };
      })
      .filter(Boolean) as { gatewayId: string; values: any }[];
  }, [readingsMap, asset?.assignedTags]);

  const filteredStates = useMemo(
    () =>
      asset?.assignedTags
        ?.filter((tag: AssignedTag) => tag.tagName.toLowerCase() === STATE)
        .sort((a, b) => a.tagIndicatorPosition - b.tagIndicatorPosition),
    [asset?.assignedTags]
  );

  const displayState =
    liveState && liveState !== '-'
      ? liveState
      : getStateOrModeData({
          data: filteredStates ?? [],
          latestReadings: readingsArray,
        });

  return { displayState };
};
